<template>
  <div ref="containerRef" class="client-search-bar">
    <div class="client-search-bar__input-wrapper" @click="focusInput">
      <Search class="client-search-bar__icon" :size="18" />
      <input
        ref="inputRef"
        v-model="searchQuery"
        type="text"
        class="client-search-bar__input"
        :placeholder="searchPlaceholder"
        autocomplete="off"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        @keydown="handleKeydown"
      />
      <kbd v-if="!isFocused && !searchQuery" class="client-search-bar__shortcut">
        {{ t('clientSearch.shortcut', { modifier: isMac ? '⌘' : 'Ctrl' }) }}
      </kbd>
      <button
        v-if="searchQuery"
        class="client-search-bar__clear"
        type="button"
        @mousedown.prevent="clearSearch"
        :aria-label="t('accessibility.clearSearch')"
      >
        <X :size="16" />
      </button>
    </div>

    <!-- Dropdown results -->
    <Teleport to="body">
      <div
        v-if="showDropdown"
        ref="dropdownRef"
        class="client-search-bar__dropdown"
        :style="positionStyle"
      >
        <div v-if="loading" class="client-search-bar__loading">
          <span class="client-search-bar__spinner"></span>
          {{ t('clientSearch.loading') }}
        </div>

        <div v-else-if="searchQuery.length > 0 && searchQuery.length < 2" class="client-search-bar__hint">
          {{ t('clientSearch.minCharsHint') }}
        </div>

        <div v-else-if="searchQuery.length >= 2 && results.length === 0" class="client-search-bar__empty">
          {{ t('clientSearch.noResults') }}
        </div>

        <div v-else-if="results.length > 0" class="client-search-bar__results">
          <button
            v-for="(client, index) in results"
            :key="client.id"
            class="client-search-bar__result"
            :class="{ 'client-search-bar__result--active': activeIndex === index }"
            @click="selectClient(client)"
            @mouseenter="activeIndex = index"
          >
            <div class="client-search-bar__result-main">
              <span class="client-search-bar__result-name">
                {{ getFullName(client) }}
              </span>
              <span v-if="client.dateOfBirth" class="client-search-bar__result-dob">
                {{ formatDateDigits(client.dateOfBirth) }}
              </span>
            </div>
            <div v-if="client.email || client.addressCity" class="client-search-bar__result-sub">
              <span v-if="client.email" class="client-search-bar__result-email">{{ client.email }}</span>
              <span v-if="client.addressCity" class="client-search-bar__result-city">{{ client.addressCity }}</span>
            </div>
          </button>
        </div>

        <div v-if="results.length > 0" class="client-search-bar__footer">
          <span class="client-search-bar__footer-hint">
            <kbd>↵</kbd> {{ t('clientSearch.footer.select') }}
            <kbd>↑↓</kbd> {{ t('clientSearch.footer.navigate') }}
            <kbd>{{ t('clientSearch.footer.escKey') }}</kbd> {{ t('clientSearch.footer.close') }}
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Search, X } from 'lucide-vue-next'
import type { ClientProfile } from '@/api/generated-clients'
import { useAnchoredPosition } from '@/composables/useAnchoredPosition'
import { useClientSearch } from '@/composables/useClientSearch'
import { useFormatters } from '@/composables/useFormatters'
import { getFullName } from '@/utils/userFormatting'

interface Props {
  placeholder?: string
}

const props = defineProps<Props>()

const { t } = useI18n()
const router = useRouter()
const { formatDateDigits } = useFormatters()

const containerRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const showDropdown = ref(false)
const isFocused = ref(false)

const {
  query: searchQuery,
  results,
  loading,
  selectedIndex: activeIndex,
  search,
  selectNext,
  selectPrevious,
  selectCurrent,
  reset
} = useClientSearch()

const isMac = computed(() => navigator.platform.toUpperCase().indexOf('MAC') >= 0)
const searchPlaceholder = computed(() => props.placeholder ?? t('clientSearch.placeholder'))

// Timeout for blur dropdown hide
const { start: startBlurTimeout } = useTimeoutFn(() => {
  showDropdown.value = false
}, 150, { immediate: false })

/**
 * The results are placed below the bar, clamped into the viewport and re-placed
 * while the page moves under them (#3700). They used to be a `computed` over the
 * container's rect, which does not re-evaluate on scroll — so the list stayed
 * where the page was when it opened — and clamped nothing, so a bar low in the
 * window put its results off the bottom.
 *
 * The bar sits in the sticky top bar and so cannot normally scroll away, but if
 * it does the list is dismissed rather than followed: results anchored to a
 * search box nobody can see belong to no visible control.
 */
const { positionStyle } = useAnchoredPosition({
  anchorEl: () => containerRef.value,
  floatingEl: dropdownRef,
  visible: () => showDropdown.value,
  onAnchorLost: () => {
    showDropdown.value = false
  },
  placement: 'below',
})

function focusInput() {
  inputRef.value?.focus()
}

function handleFocus() {
  isFocused.value = true
  if (searchQuery.value.length >= 2 && results.value.length > 0) {
    showDropdown.value = true
  } else if (searchQuery.value.length > 0) {
    showDropdown.value = true
  }
}

function handleBlur() {
  isFocused.value = false
  // Delay hiding to allow click events on dropdown items
  startBlurTimeout()
}

function handleInput() {
  if (searchQuery.value.length < 2) {
    showDropdown.value = searchQuery.value.length > 0
  } else {
    showDropdown.value = true
  }
  search()
}

function handleKeydown(event: KeyboardEvent) {
  // Handle Enter key first to prevent event bubbling regardless of dropdown state
  if (event.key === 'Enter') {
    event.preventDefault()
    if (showDropdown.value && results.value.length > 0) {
      const selectedClient = selectCurrent()
      if (selectedClient) {
        selectClient(selectedClient)
      }
    }
    return
  }

  if (!showDropdown.value || results.value.length === 0) {
    if (event.key === 'Escape') {
      inputRef.value?.blur()
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      selectNext()
      break
    case 'ArrowUp':
      event.preventDefault()
      selectPrevious()
      break
    case 'Escape':
      event.preventDefault()
      showDropdown.value = false
      inputRef.value?.blur()
      break
  }
}

function selectClient(client: ClientProfile) {
  reset()
  showDropdown.value = false
  inputRef.value?.blur()
  router.push({ name: 'client-detail', params: { clientId: client.id } })
}

function clearSearch() {
  reset()
  showDropdown.value = false
  inputRef.value?.focus()
}

// Global keyboard shortcut
function handleGlobalKeydown(event: KeyboardEvent) {
  const isCmdOrCtrl = isMac.value ? event.metaKey : event.ctrlKey
  if (isCmdOrCtrl && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    inputRef.value?.focus()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})

// Expose focus method for external use
defineExpose({
  focus: focusInput
})
</script>

<style scoped>
/*
 * The results are teleported to `body`, so they cannot inherit the bar's width
 * the way they inherit its position. Both are bounded by the same two numbers,
 * stated once: never narrower than the bar, never wider than it can grow.
 */
.client-search-bar,
.client-search-bar__dropdown {
  min-width: 400px;
  max-width: 560px;
}

.client-search-bar {
  position: relative;
  width: 100%;
}

.client-search-bar__input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  height: 36px;
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background: var(--color-background-content);
  cursor: text;
  transition: all var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.client-search-bar__input-wrapper:hover {
  border-color: var(--color-border-input);
}

.client-search-bar__input-wrapper:focus-within {
  border-color: var(--color-brand-primary);
  box-shadow: var(--shadow-focus);
}

.client-search-bar__icon {
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.client-search-bar__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: var(--font-size-sm);
  font-family: inherit;
  color: var(--color-text-primary);
  background: transparent;
}

.client-search-bar__input::placeholder {
  color: var(--color-text-secondary);
}

.client-search-bar__shortcut {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  font-size: var(--font-size-xs);
  font-family: inherit;
  color: var(--color-text-secondary);
  background: var(--color-background-secondary);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-sm);
  white-space: nowrap;
}

.client-search-bar__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.client-search-bar__clear:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}

/* Dropdown styles — `useAnchoredPosition` supplies `top`/`left` only. */
.client-search-bar__dropdown {
  position: fixed;
  z-index: 9999;
  width: max-content;
  background: var(--color-background-content);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  max-height: 400px;
  overflow-y: auto;
}

.client-search-bar__loading,
.client-search-bar__hint,
.client-search-bar__empty {
  padding: var(--spacing-md);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.client-search-bar__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.client-search-bar__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-border-default);
  border-top-color: var(--color-brand-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.client-search-bar__results {
  padding: var(--spacing-xs);
}

.client-search-bar__result {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.client-search-bar__result:hover,
.client-search-bar__result--active {
  background: var(--color-background-secondary);
}

.client-search-bar__result-main {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.client-search-bar__result-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.client-search-bar__result-dob {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.client-search-bar__result-sub {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.client-search-bar__result-email,
.client-search-bar__result-city {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.client-search-bar__footer {
  padding: var(--spacing-xs) var(--spacing-md);
  border-top: 1px solid var(--color-border-default);
  background: var(--color-background-secondary);
}

.client-search-bar__footer-hint {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.client-search-bar__footer-hint kbd {
  display: inline-flex;
  align-items: center;
  padding: 1px 4px;
  font-size: var(--font-size-xs);
  font-family: inherit;
  background: var(--color-background-content);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-sm);
  margin-right: 2px;
}

/*
 * Below lg the bar shares its row with the hamburger (`TopBar`, #4072), so it
 * gives up its floor and takes whatever the row can spare. The dropdown keeps
 * one — a result list narrower than its rows reads worse than a wide one — but
 * bounded by the viewport: it is `position: fixed`, and `useAnchoredPosition`
 * clamps where it is placed and not how wide it is, so a 400px list under a
 * 360px window would otherwise run off the right edge. The slack it is held
 * away from the edges by is wider than the 8px that composable keeps, because
 * `100vw` counts a vertical scrollbar the visual viewport does not have.
 */
@media (--breakpoint-lg-down) {
  .client-search-bar {
    min-width: 0;
  }

  .client-search-bar__dropdown {
    min-width: min(400px, calc(100vw - 2 * var(--spacing-xl)));
    max-width: calc(100vw - 2 * var(--spacing-xl));
  }
}
</style>
